import { useEffect, useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Stack,
  useDisclosure
} from "@chakra-ui/react";
import { Form, Select, Tooltip } from "antd";
import { BiSlider } from "react-icons/bi";
import styled from "styled-components";
import { useFilters } from "core/features/filters/store";
import { FiltrosAvancado } from "../../../interfaces/Planejamento";
import useAgendaCampo from "../../../hooks/useAgendaCampo";
import { limitarCaracteres } from "../../ModalPlanejamentoOperacoes/utils";

type Props = {
  filtersData?: FiltrosAvancado;
}

const { useForm, Item } = Form;

export function FiltroAgendaCampoOpcional({ filtersData }: Props) {
  const [form] = useForm();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { filters, updateField } = useFilters();
  const {
    viewMode,
    listWindowExecution,
    selectedWindowIds,
    setSelectedWindowIds,
  } = useAgendaCampo();
  const { filtersAdvanced } = filters;
  const [draftSelectedWindowIds, setDraftSelectedWindowIds] = useState<string[]>([]);

  const listDateAndColors = listWindowExecution.map((item) => ({
    ...item,
    operacaoClasse: item.operacaoClasse || "",
  }));

  const checkedAll =
    listDateAndColors.length > 0 &&
    draftSelectedWindowIds.length === listDateAndColors.length;

  function handleSearch() {
    if (viewMode === "janela-execucao") {
      setSelectedWindowIds(draftSelectedWindowIds);
    }

    onClose();
  }

  function handleClear() {
    updateField("filtersAdvanced", {
      ...filtersAdvanced,
      classeOperacao: [],
    });

    form.resetFields();

    if (viewMode === "janela-execucao") {
      setDraftSelectedWindowIds(listDateAndColors.map((item) => item.id));
    }
  }

  function handleSelectAll() {
    if (checkedAll) {
      setDraftSelectedWindowIds([]);
      return;
    }
    setDraftSelectedWindowIds(listDateAndColors.map((item) => item.id));
  }

  function handleSelectWindowItem(id: string) {
    if (draftSelectedWindowIds.includes(id)) {
      setDraftSelectedWindowIds(draftSelectedWindowIds.filter((item) => item !== id));
      return;
    }
    setDraftSelectedWindowIds([...draftSelectedWindowIds, id]);
  }

  useEffect(() => {
    if (isOpen) {
      setDraftSelectedWindowIds(selectedWindowIds);
    }
  }, [isOpen, selectedWindowIds]);

  useEffect(() => {
    if (!filtersAdvanced?.cultura && !filtersAdvanced?.classeOperacao) {
      form.resetFields();
    }
  }, [!filtersAdvanced?.cultura, !filtersAdvanced?.classeOperacao]);

  if (viewMode !== "janela-execucao") return null;

  return (
    <Popover placement="bottom-start" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <ButtonSliderWrapper>
          {draftSelectedWindowIds?.length > 0 && <IconCircle>{draftSelectedWindowIds?.length}</IconCircle>}
          <BiSlider />
        </ButtonSliderWrapper>
      </PopoverTrigger>
      <Form
        form={form}
        layout="vertical"
      >
        <PopoverContent p={4} w="sm">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold">Filtro Opcional</PopoverHeader>
          <PopoverBody>
            <Stack spacing={2}>
              <Wrap>
                <Item
                  label="Classe de Operação"
                  name="classeOperacao"
                >
                  <SelectAntd
                    allowClear
                    showSearch
                    mode="multiple"
                    maxTagCount={2}
                    maxTagPlaceholder={(omittedValues) => <span>+{omittedValues.length}</span>}
                    placeholder="Classe de Operação"
                    optionFilterProp="label"
                    defaultValue={filtersAdvanced?.classeOperacao}
                    options={(filtersData?.operacaoClasses || [])?.map((item: any) => ({
                      label: item.descricao,
                      value: item.operacaoClasseId,
                    }))}
                  />
                </Item>
              </Wrap>
              {
                viewMode === "janela-execucao" && (
                  <Wrap>
                    <TitleWindowExecution>Janela de Execução</TitleWindowExecution>
                    {
                      listDateAndColors.length > 0 && (
                        <div>
                          <DisplayColorsDate>
                            <label>
                              <input
                                type="checkbox"
                                checked={checkedAll}
                                onChange={handleSelectAll}
                              /> Todos
                            </label>
                          </DisplayColorsDate>
                          <DisplayColorsDate style={{ marginTop: 10 }}>
                            {
                              listDateAndColors.map((item) => (
                                <label key={item.id}>
                                  <input
                                    type="checkbox"
                                    checked={draftSelectedWindowIds.includes(item.id)}
                                    onChange={() => handleSelectWindowItem(item.id)}
                                  />
                                  <CheckColor backgroundColor={item?.color} />
                                  <div style={{ display: "flex", gap: 5, justifyContent: "space-between", width: "100%" }}>
                                    <ItemLabelDate>{item.periodoDataInicio} a {item.periodoDataFim}</ItemLabelDate>
                                    <Tooltip title={item.operacaoClasse}><ItemLabelDate>{limitarCaracteres(item.operacaoClasse, 15, true)}</ItemLabelDate></Tooltip>
                                  </div>
                                </label>
                              ))
                            }
                          </DisplayColorsDate>
                        </div>
                      )
                    }
                  </Wrap>
                )
              }

            </Stack>
          </PopoverBody>

          <PopoverFooter display="flex" justifyContent="space-between">
            <Button variant="ghost" onClick={handleClear}>Limpar</Button>
            <Button colorScheme="green" onClick={handleSearch}>Aplicar</Button>
          </PopoverFooter>
        </PopoverContent>
      </Form>
    </Popover>
  );
}

const ButtonSliderWrapper = styled.button`
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    min-width: 45px;
    height: 60px;

    svg {
      font-size: 20px;
    }

    :active {
        background: #20a45a;
        border-color: #20a45a;
    }
`;

const Wrap = styled.div`
    width: 100%;

    .ant-form-item {
      margin-bottom: 0;
    }

    .ant-col {
      padding: 0;
    }
`;

export const SelectAntd = styled(Select)`
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    padding: 4px 11px !important;
  }
`;

export const TitleWindowExecution = styled.h3`
  margin-bottom: 10px;
  padding: 5px 0;
`

export const DisplayColorsDate = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }
`;

export const CheckColor = styled.span<{ backgroundColor?: string }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: ${(props) => props.backgroundColor};

  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
`;

export const ItemLabelDate = styled.div`
  white-space: nowrap;
  text-align: left;
`;

const IconCircle = styled.span`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 2px;
  right: 1px;
  border-radius: 50%;
  background-color: #ff0000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;